// Gemini Service - Now routes through Supabase Edge Functions
// This solves geographic restrictions and keeps API keys server-side

import {
  Language,
  LanguageCode,
  JournalistAgentResponse,
  CriticAgentResponse,
  SocialAgentResponse,
  EditorIssue,
  BCP47_TO_LEGACY,
} from '../types';
import { supabase } from './supabaseClient';

// Re-export types for backwards compatibility
export type JournalistResponse = JournalistAgentResponse;
export type CriticResponse = CriticAgentResponse;
export type SocialPosts = SocialAgentResponse;

// Get the Supabase URL for Edge Functions
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

/**
 * Helper to call an Edge Function with authentication
 */
async function callEdgeFunction<T>(functionName: string, payload: object): Promise<T | null> {
  try {
    // Get the current session for the auth token
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      console.error('No active session for Edge Function call:', sessionError);
      return null;
    }

    const response = await fetch(`${SUPABASE_URL}/functions/v1/${functionName}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Edge Function ${functionName} failed:`, response.status, errorText);
      return null;
    }

    const data = await response.json();
    
    // Check if the function returned an error
    if (data.error) {
      console.error(`Edge Function ${functionName} returned error:`, data.error);
    }
    
    return data as T;
  } catch (error) {
    console.error(`Edge Function ${functionName} call failed:`, error);
    return null;
  }
}

// --- AGENT 1: The Journalist (Writer/Fixer) ---
// Supports both old Language enum and new LanguageCode
export async function runJournalistAgent(
  headline: string,
  deck: string,
  body: string,
  language: Language | LanguageCode,
  feedback: string[] = []
): Promise<JournalistResponse> {
  // Convert LanguageCode to Language enum for the edge function
  const langValue = typeof language === 'string' && language.includes('-')
    ? BCP47_TO_LEGACY[language as LanguageCode]
    : language;

  console.log('Calling journalist-agent with language:', langValue);

  const result = await callEdgeFunction<JournalistResponse>('journalist-agent', {
    headline,
    deck,
    body,
    language: langValue,
    feedback
  });

  console.log('Journalist agent result:', result);

  // Return original content if the call failed
  return result || { headline, deck, body };
}

// --- AGENT 2: The Critic (Reviewer) ---
// Supports both old Language enum and new LanguageCode
export async function runCriticAgent(
  headline: string,
  deck: string,
  body: string,
  language: Language | LanguageCode,
  isTranslation: boolean = false
): Promise<CriticResponse> {
  // Convert LanguageCode to Language enum for the edge function
  const langValue = typeof language === 'string' && language.includes('-')
    ? BCP47_TO_LEGACY[language as LanguageCode]
    : language;

  console.log('Calling critic-agent with language:', langValue);

  const result = await callEdgeFunction<CriticResponse>('critic-agent', {
    headline,
    deck,
    body,
    language: langValue,
    isTranslation
  });

  console.log('Critic agent result:', result);

  return result || { issues: [] };
}

// --- AGENT 3: Social Media ---
export async function runSocialAgent(
  headline: string, 
  body: string, 
  language: Language = Language.ENGLISH
): Promise<SocialPosts> {
  const result = await callEdgeFunction<SocialPosts>('social-agent', {
    headline,
    body,
    language
  });
  
  return result || { posts: [] };
}

// --- AGENT 4: Translator ---
export async function runTranslatorAgent(
  headline: string, 
  deck: string, 
  body: string, 
  sourceLang: Language, 
  targetLang: Language
): Promise<JournalistResponse> {
  const result = await callEdgeFunction<JournalistResponse>('translator-agent', {
    headline,
    deck,
    body,
    sourceLang,
    targetLang
  });
  
  return result || { headline, deck, body };
}

// --- Headline Suggestion ---
export async function suggestHeadline(
  body: string, 
  language: Language
): Promise<{ headline: string; deck: string }> {
  const result = await callEdgeFunction<{ headline: string; deck: string }>('suggest-headline', {
    body,
    language
  });
  
  return result || { headline: '', deck: '' };
}

// --- Legacy helper (still works, routes through journalist agent) ---
export async function polishStory(body: string): Promise<string> {
  const res = await runJournalistAgent('', '', body, Language.ENGLISH, []);
  return res.body || body;
}
